import React, { useState, createContext, useRef, useContext, useEffect, useCallback, useMemo } from 'react';
import _ from "lodash";

import { isInvalid as isInvalidFunction, buildValidationMessage, isRecommendedInvalid } from './helper';

export const ValidationContext = createContext();

/**
 * useForm hook to manage the validation state and logic for a detail.
 * @param {Function} customValidation - Custom validation function to be applied after built-in validation.
 * @param {Object} formData - Full form data used for isInvalidFunction.
 * @returns {Object} - Returns an object containing invalidOrRecommendedInvalidFields, updateValidationState, and validate.
 */
export const useForm = (customValidation, formData) => {
  const validationStateRef = useRef([]);
  const [invalidFields, setInvalidFields] = useState([]);
  const [invalidRecommended, setInvalidRecommended] = useState([]);

  const registerValidationInput = (field, getValue, unregister) => {
    if (unregister) {
      validationStateRef.current = validationStateRef.current.filter(x => x.field.field !== field.field);  
    }
    else {
      const item = invalidFields.find(x => x.name === field.field);
      if (item && field.validationMessage && item.validationMessage !== field.validationMessage) {
        item.validationMessage = field.validationMessage;
      }
      validationStateRef.current = validationStateRef.current.filter(x => x.field.field !== field.field);
      validationStateRef.current = [...validationStateRef.current, { field, getValue }];
    }
  };

  /**
  * Updates the validation state for a specific field.
  * @param {string} name - The name of the field.
  * @param {string} type - The type of the field.
  * @param {Function} validate - The validation function for the field.
  * @param {boolean} isInvalid - Whether the field is invalid.
  * @param {boolean} isRecommendedInvalid - Whether the field is recommendedInvalid.
  * @param {string} validationMessage - The validation message for the field.
  * @param {string} title - The title of the field.
  * @param {Function} getValue - Function to get the current value of the field.
  */
  const updateValidationState = useCallback((name, isInvalid, isRecommendedInvalid, validationMessage, title) => {
    setInvalidFields(prevFields => {
      const existingFieldIndex = prevFields.findIndex(field => field.name === name);

      if (isInvalid || isRecommendedInvalid) {
        if (existingFieldIndex !== -1) {
          // Update the existing field
          const updatedFields = [...prevFields];
          updatedFields[existingFieldIndex] = {
            ...updatedFields[existingFieldIndex],
            isInvalid,
            isRecommendedInvalid,
            validationMessage
          };
          return updatedFields;
        } else {
          return [...prevFields, { name, title, isInvalid, isRecommendedInvalid, validationMessage }];
        }
      } else {
        // Remove the field if it exists and is no longer invalid
        return existingFieldIndex !== -1 ? prevFields.filter(f => f.name !== name) : prevFields;
      }
    });

  }, []);

  const cleanValidationState = useCallback(() => {
    setInvalidFields([]);
  }, []);

  /**
  * Validates if passwords are matching or not.
  * @returns {boolean} - Returns true if passwords are invalid, otherwise false.
  */
  const arePasswordsInvalid = useCallback(async () => {
    const passwords = validationStateRef.current.filter(item => item.field.type === "password");
    let arePasswordsInvalid = false;

    if (passwords.length === 2) {
      const [firstPassword, secondPassword] = passwords;

      const passwordsMismatch = firstPassword.getValue() !== secondPassword.getValue();

      updateValidationState(secondPassword.field.field, passwordsMismatch, false, buildValidationMessage(secondPassword.field, secondPassword.getValue(), true), secondPassword.field.title);
      if (passwordsMismatch) {
        arePasswordsInvalid = true;
      }
    }

    return arePasswordsInvalid;
  }, [updateValidationState]);

  /**
  * Runs validation on all fields and custom validation if provided.
  * @returns {boolean} - Returns true if any fields are invalid, otherwise false.
  */
  const validate = useCallback(async () => {
    let areFieldsInvalid = false;
    let recommended = [];

    for (const item of validationStateRef.current) {
      const field = item.field;
      const value = item.getValue();
      const isInvalid = await isInvalidFunction(field, value, formData);
      const isRecommended = isRecommendedInvalid(field, value);
      updateValidationState(field.field, isInvalid, isRecommended, buildValidationMessage(field, value), field.title);

      if (isInvalid) {
        areFieldsInvalid = true;
      }
      if (isRecommended) {
        recommended = [...recommended, field];
      }

      if (field.type === "password") {
        if (await arePasswordsInvalid()) {
          areFieldsInvalid = true;
        }
      }

      if (customValidation) {
        if (await customValidation(item.field)) {
          areFieldsInvalid = true;
          updateValidationState(item.field.field, true, isRecommendedInvalid(item.field, item.getValue()), buildValidationMessage(item.field, item.getValue()), item.field.title);
        }
      }
    }
    if (!areFieldsInvalid && invalidRecommended.length > 0) {
      setInvalidRecommended(recommended);
      return true;
    }

    return areFieldsInvalid;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customValidation, updateValidationState, arePasswordsInvalid, formData]);

  const validateField = useCallback(async (field, value, formData) => {
    let isInvalid = await isInvalidFunction(field, value, formData);
    const isRecommended = isRecommendedInvalid(field, value);
    updateValidationState(field.field, isInvalid, isRecommended, buildValidationMessage(field, value), field.title);

    if (customValidation) {
      if (await customValidation(field)) {
        isInvalid = true;
        updateValidationState(field.field, true, isRecommendedInvalid(field, value), buildValidationMessage(field, value), field.title);
      }
    }

    return isInvalid;
  }, [customValidation, updateValidationState]);

  const addInvalid = useCallback((field) => {
    updateValidationState(field.field, true, false, "", field.title);
  }, [updateValidationState]);

  return {
    invalidFields,
    updateValidationState,
    validate,
    registerValidationInput,
    invalidRecommended,
    setInvalidRecommended,
    cleanValidationState,
    customValidation,
    arePasswordsInvalid,
    validateField,
    addInvalid,
    formData
  };
};

/**
 * useValidation hook to manage the validation logic for an individual field.
 * @param {Object} field - The field object containing information like field name, type, title, etc.
 * @param {*} defaultValue - The default value of the field.
 * @returns {Object} - Returns an object containing validate and setValidationValue.
 */
export const useValidation = (field, defaultValue) => {
  const context = useContext(ValidationContext);
  const registerValidationInput = useMemo(() => context?.registerValidationInput || (() => { }), [context]);
  const updateValidationState = useMemo(() => context?.updateValidationState || (() => { }), [context]);
  const customValidation = useMemo(() => context?.customValidation || null, [context]);
  const arePasswordsInvalid = useMemo(() => context?.arePasswordsInvalid || (() => { }), [context]);
  const formData = useMemo(() => context?.formData || null, [context]);
  const valueRef = useRef(defaultValue);

  /**
  * Sets the current value of the field.
  * @param {*} newValue - The new value of the field.
  */
  const setValidationValue = useCallback((newValue) => {
    valueRef.current = newValue;
  }, []);

  /**
   * Gets the current value of the field.
   * @returns {*} - Returns the current value of the field.
   */
  const getValue = useCallback(() => {
    return valueRef.current;
  }, []);

  /**
  * Validates the field based on the provided isFieldInvalid function and recommended invalidation logic.
  * @returns {boolean} - Returns true if the field is invalid, otherwise false.
  */
  const validate = useCallback(async () => {
    if (!field.validationAllowed) return;
    const value = getValue();
    let invalid = await isInvalidFunction(field, value, formData);
    if (!invalid && customValidation) {
      invalid = await customValidation(field);
    }
    if (field.type === "password") {
      await arePasswordsInvalid();
    }
    updateValidationState(field.field, invalid, isRecommendedInvalid(field, value), buildValidationMessage(field, value), field.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field, getValue, updateValidationState, customValidation, arePasswordsInvalid, formData]);

  useEffect(() => {
    if (!field.validationAllowed) return;

    if (!_.isEqual(defaultValue, valueRef.current)) {
      setValidationValue(defaultValue);
      if (defaultValue && !(Array.isArray(defaultValue) && defaultValue.length === 0)) {
        validate();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue, setValidationValue]);

  useEffect(() => {
    if (!field.validationAllowed) return;

    registerValidationInput(field, getValue);
  }, [field, getValue, registerValidationInput]);

  if (!context) {
    return {
      validate: () => { },
      setValidationValue: () => { }
    };
  }

  return { validate, setValidationValue };
};

/**
 * ValidationProvider component to provide validation-related state and functions to its children.
 * @param {Array} invalidFields - Array of invalid or recommended invalid fields.
 * @param {Function} updateValidationState - Function to update the validation state.
 * @param {React.ReactNode} children - The children components.
 * @param {Object} formData - Full form data used for isInvalidFunction.
 * @returns {JSX.Element} - The ValidationProvider component.
 */
const ValidationProvider = ({ invalidFields, registerValidationInput, updateValidationState, customValidation, arePasswordsInvalid, children, formData }) => {
  /**
   * Checks if a field is invalid or recommended invalid.
   * @param {string} field - The name of the field.
   * @returns {Object|null} - Returns the field object if invalid or recommended invalid, otherwise null.
   */
  const getIsInvalid = useCallback((field) => {
    return invalidFields.find(f => f.name === field) || null;
  }, [invalidFields]);

  /**
  * Gets the titles of all invalid or recommended invalid fields.
  * @returns {Array<string>} - Returns an array of titles.
  */
  const getInvalidFieldsTitles = useCallback(() => {
    return invalidFields.map(field => field.title);
  }, [])

  return (
    <ValidationContext.Provider value={{ invalidFields, updateValidationState, registerValidationInput, customValidation, arePasswordsInvalid, getIsInvalid, getInvalidFieldsTitles, formData }}>
      {children}
    </ValidationContext.Provider>
  );
};

export default ValidationProvider;